import {
  CommonHelperFunctions,
  DashboardState,
  EScreenType,
  LeftPanelType,
  RightPanelType
} from "@kuika/kuika-cl-model";
import React from "react";
import { connect } from "react-redux";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";

export interface IShortCutInfoState {
  isOpen: boolean;
}

export interface IShortCutInfoProps {
  setNextCallbackFunction: (cb?: () => void) => void;
}

class ShortCutInfoComponent extends React.PureComponent<
  IShortCutInfoProps & IWithSDProps & IWithContextProp,
  IShortCutInfoState
> {
  state = {
    isOpen: false
  };

  handleSave = async () => {
    await this.props.sd.saveScreen();
  };

  handleUndo = () => {
    if (this.props.sd.canUndo()) {
      this.props.sd.undo();
    }
  };

  handleRedo = () => {
    if (this.props.sd.canRedo()) {
      this.props.sd.redo();
    }
  };

  handleDuplicate = () => {
    this.props.sd.cloneDI(this.props.sd.getActiveKuikaComponent()?.kuikaId);
  };

  handleDelete = () => {
    const kuikaId = this.props.sd.getActiveKuikaComponent()?.kuikaId;
    const sd = this.props.sd.getScreenDesign();
    const isEditableTableColumnChild = CommonHelperFunctions.getIsEditableTableColumnChild(kuikaId, sd?.displayItems);
    if (isEditableTableColumnChild) {
      return;
    }
    if (kuikaId) {
      this.props.sd.deleteItemByKuikaId(kuikaId);
    }
  };

  hasUnsavedChanges = (): boolean => {
    const { sd } = this.props;
    return !!(
      sd?.activeConfiguration ||
      sd?.activeEditingDatasource ||
      sd?.activeCreatingDatasourceType !== undefined ||
      sd?.editingTable ||
      sd?.editingAction ||
      sd?.activeCreatingTableName ||
      sd?.activeCreatingActionName ||
      sd?.editingNotificationTemplate ||
      sd?.selectedSjId ||
      sd?.getSelectedApi() ||
      sd.dashboardState === DashboardState.runSettings
    );
  };

  async reloadScreenList() {
    try {
      await this.props.sd.setScreenList();
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    }
  }

  loadScreen = async () => {
    const { sd } = this.props;
    const isSelectedScreenIsReportDesign = sd.isReportDesignPageSelected();
    const isComingFromReportDesigner = sd.dashboardState === DashboardState.reportDesigner;

    if (isSelectedScreenIsReportDesign || isComingFromReportDesigner) {
      if (isSelectedScreenIsReportDesign) {
        await sd.saveScreen(true);
      }
      await this.reloadScreenList();

      const { screenList, project } = sd;
      const { lastVisitedScreenId } = project;

      if (lastVisitedScreenId) {
        sd.loadScreen(lastVisitedScreenId, true);
      } else {
        const screenId = screenList.find(
          (x) =>
            x.publicMetaData.screenType !== EScreenType.Report &&
            x.publicMetaData.screenType !== EScreenType.Master &&
            x.publicMetaData.screenType !== EScreenType.Component
        )?.id;
        if (screenId) {
          sd.loadScreen(screenId, true);
        }
      }
    }
  };

  handleRedirect = async (
    targetState: DashboardState,
    extraSetup: () => void = () => {},
    preCheck: boolean = false,
    loadScreen: boolean = false
  ) => {
    const { sd } = this.props;
    if (sd.dashboardState === targetState) return;

    // Decided on the click: with an editor open, `redirect` runs later, from inside the editor as it
    // closes, and by then what this reads may have moved.
    const isReturningToDesignFromReport: boolean =
      targetState === DashboardState.design &&
      (sd.dashboardState === DashboardState.reportDesigner || sd.isReportDesignPageSelected());

    // Both branches below run all of this — see toolbar-left.tsx `handleRedirect`, which this mirrors:
    // the tool's own preparation sitting in the direct branch alone is what let an open editor skip it.
    const redirect = async (): Promise<void> => {
      if (isReturningToDesignFromReport) {
        await this.loadScreen();
      } else if (targetState === DashboardState.reportDesigner) {
        await this.props.sd.saveScreen(true);
      }
      extraSetup();
      if (targetState !== DashboardState.notificationTemplate) {
        this.props.sd.clearNotificationTemplatePanel();
      }
      if (targetState !== DashboardState.scheduledJobs) {
        this.props.sd.cancelEditingScheduledJob();
      }
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      // Don't clear the workflow selection when entering the Workflow tab — extraSetup restores
      // lastOpenWfId and runs before this clear (KOA-10769 regression; see toolbar-left.tsx).
      if (targetState !== DashboardState.workFlow) {
        this.props.sd.clearSelectedWf();
      }
      if (loadScreen) {
        await sd.saveScreen(true);
        await this.reloadScreenList();
      }
      if (targetState === DashboardState.reportDesigner) {
        this.props.sd.enterReportDesigner({ showLanding: true });
      } else {
        sd.setDashboardState(targetState);
      }
    };

    if (preCheck || this.hasUnsavedChanges()) {
      this.props.setNextCallbackFunction(redirect);
    } else {
      await redirect();
    }
  };

  handleNavigateToUIDesign = () => {
    this.handleRedirect(DashboardState.design, () => {
      this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
    });
  };

  handleNavigateToDatasources = () => {
    this.handleRedirect(DashboardState.dataDiagramScheme);
  };

  handleNavigateToProcessAutomation = () => {
    this.handleRedirect(DashboardState.workFlow, () => {
      this.props.sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowList);
      // Re-select the last-open workflow so the selection persists across tab switches.
      if (this.props.sd.lastOpenWfId) this.props.sd.setSelectedWf(this.props.sd.lastOpenWfId);
      else this.props.sd.setCreatingWfActive(true);
    });
  };

  handleNavigateToEmailBuilder = () => {
    this.handleRedirect(DashboardState.notificationTemplate, () => {
      this.props.sd.setCreateNotificationTemplateActive();
    });
  };

  handleNavigateToReportDesigner = () => {
    this.handleRedirect(DashboardState.reportDesigner);
  };

  handleNavigateToScheduledJobs = () => {
    this.handleRedirect(DashboardState.scheduledJobs, async () => {
      this.props.sd.setCreateSJobActive(true);
      await this.props.sd.loadActionDefinitions(true);
    });
  };

  handleNavigateToAPIBuilder = () => {
    this.handleRedirect(DashboardState.apiManagement, () => {
      this.props.sd.setActiveApiManagementPanel(LeftPanelType.ApiManagement);
    });
  };

  handleNavigateToConfigurationManager = () => {
    this.handleRedirect(DashboardState.configManager, () => {
      this.props.sd.cancelCreatingOrEditingConfiguration();
    });
  };

  handleRedirectToHelpDesk() {
    if (this.props.sd.dashboardState === DashboardState.zendesk) return;

    const redirectToHelpDesk = () => {
      this.props.sd.setDashboardState(DashboardState.zendesk);

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.selectedSjId ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToHelpDesk);
      return;
    }

    redirectToHelpDesk();
  }

  handleMenuItemClick = (key: string) => {
    switch (key) {
      case "undo":
        this.handleUndo();
        break;
      case "redo":
        this.handleRedo();
        break;
      case "duplicate":
        this.handleDuplicate();
        break;
      case "delete":
        this.handleDelete();
        break;
      case "save":
        this.handleSave();
        break;
      case "ui-design":
        this.handleNavigateToUIDesign();
        break;
      case "datasources":
        this.handleNavigateToDatasources();
        break;
      case "process-automation":
        this.handleNavigateToProcessAutomation();
        break;
      case "email-builder":
        this.handleNavigateToEmailBuilder();
        break;
      case "report-designer":
        this.handleNavigateToReportDesigner();
        break;
      case "scheduled-jobs":
        this.handleNavigateToScheduledJobs();
        break;
      case "api-builder":
        this.handleNavigateToAPIBuilder();
        break;
      case "configuration-manager":
        this.handleNavigateToConfigurationManager();
        break;
      case "helpdesk":
        this.handleRedirectToHelpDesk();
        break;
    }
    this.setState({ isOpen: false });
  };

  getCtrlKeyString = () => {
    if (CommonHelperFunctions.isMac()) return "Cmd";
    return "Ctrl";
  };

  getAltKeyString = () => {
    if (CommonHelperFunctions.isMac()) return "Opt";
    return "Alt";
  };

  getShortcutOptions = (): KuikaDropdownMenuItemType[] => {
    return [
      {
        group: "EDIT",
        key: "undo",
        label: "Undo",
        rightSlot: `${this.getCtrlKeyString()} + Z`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("undo")
      },
      {
        group: "EDIT",
        key: "redo",
        label: "Redo",
        rightSlot: `${this.getCtrlKeyString()} + Y`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("redo")
      },
      {
        group: "EDIT",
        key: "duplicate",
        label: "Duplicate",
        rightSlot: `${this.getCtrlKeyString()} + D`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("duplicate")
      },
      {
        group: "EDIT",
        key: "delete",
        label: "Delete",
        rightSlot: "Delete",
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("delete")
      },
      {
        group: "SCREENS",
        key: "save",
        label: "Save",
        rightSlot: `${this.getCtrlKeyString()} + S`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("save")
      },
      {
        group: "GO TO",
        key: "ui-design",
        label: "UI Design",
        rightSlot: `${this.getAltKeyString()} + 1`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("ui-design")
      },
      {
        group: "GO TO",
        key: "datasources",
        label: "Datasources",
        rightSlot: `${this.getAltKeyString()} + 2`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("datasources")
      },
      {
        group: "GO TO",
        key: "process-automation",
        label: "Process Automation",
        rightSlot: `${this.getAltKeyString()} + 3`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("process-automation")
      },
      {
        group: "GO TO",
        key: "email-builder",
        label: "Email Builder",
        rightSlot: `${this.getAltKeyString()} + 4`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("email-builder")
      },
      {
        group: "GO TO",
        key: "report-designer",
        label: "Report Designer",
        rightSlot: `${this.getAltKeyString()} + 5`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("report-designer")
      },
      {
        group: "GO TO",
        key: "scheduled-jobs",
        label: "Scheduled Jobs",
        rightSlot: `${this.getAltKeyString()} + 6`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("scheduled-jobs")
      },
      {
        group: "GO TO",
        key: "api-builder",
        label: "API Builder",
        rightSlot: `${this.getAltKeyString()} + 7`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("api-builder")
      },
      {
        group: "GO TO",
        key: "configuration-manager",
        label: "Configuration Manager",
        rightSlot: `${this.getAltKeyString()} + 8`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("configuration-manager")
      },
      {
        group: "GO TO",
        key: "helpdesk",
        label: "Helpdesk",
        rightSlot: `${this.getAltKeyString()} + 9`,
        showSelectedIcon: false,
        onClick: () => this.handleMenuItemClick("helpdesk")
      }
    ];
  };

  render() {
    return (
      <KuikaDropdownMenu
        options={this.getShortcutOptions()}
        open={this.state.isOpen}
        setOpen={(open) => this.setState({ isOpen: open })}
        trigger="hover"
        placement="topRight"
      >
        {(open) => (
          <KuikaButton
            data-testid="footerShortCutInfoButton"
            type="tertiary"
            size="md"
            isIcon
            color="neutral"
            active={open}
            className="kuika-left-menu__academy-button kuika-short-cut-info-button"
          >
            <KuikaSvg name="keyboard" width={18} height={18} className="kuika-short-cut-info-button-icon" />
          </KuikaButton>
        )}
      </KuikaDropdownMenu>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const shortCutInfo = connect(
  null,
  mapDispatchToProps
)(withContext<IShortCutInfoProps>(withSD<IShortCutInfoProps>(ShortCutInfoComponent)));

export { shortCutInfo as ShortCutInfo };
